/**
 * Redirect Configuration
 *
 * Defines role-based redirect URLs for post-authentication navigation.
 * After successful authentication, users are redirected to the appropriate
 * downstream application based on their role.
 *
 * Migration Note (2026-02-22):
 * Extracted from msalConfig.ts to support Firebase authentication.
 */

/**
 * Helper to read environment variables with fallback
 */
function getEnvVar(key: string, defaultValue: string): string {
  const value = import.meta.env[key];
  return typeof value === 'string' && value.length > 0 ? value : defaultValue;
}

/**
 * Application redirect URLs based on user role
 */
export const roleRedirectUrls = {
  admin: getEnvVar('VITE_ADMIN_APP_URL', 'https://admin.hirenow.com'),
  partner: getEnvVar('VITE_PARTNER_APP_URL', 'https://partner.hirenow.com'),
  supplier: getEnvVar('VITE_SUPPLIER_APP_URL', 'https://supplier.hirenow.com'),
  hirer: getEnvVar('VITE_HIRER_APP_URL', 'https://hirer.hirenow.com'),
  user: getEnvVar('VITE_USER_APP_URL', 'https://user.hirenow.com'),
} as const;

/**
 * Role priority for redirect determination
 * Higher priority roles are checked first
 */
const rolePriority: Array<keyof typeof roleRedirectUrls> = [
  'admin',
  'partner',
  'supplier',
  'hirer',
];

/**
 * Get the appropriate redirect URL based on user roles
 *
 * Checks roles in priority order (admin > partner > supplier > hirer)
 * and returns the URL for the highest-priority matching role.
 *
 * @param roles - Array of roles from the ID token custom claims
 * @returns The URL to redirect to after authentication
 */
export function getRedirectUrlForRoles(roles: string[]): string {
  // Normalize roles to lowercase for comparison
  const normalizedRoles = roles.map((r) => r.toLowerCase());

  // Check roles in priority order
  for (const role of rolePriority) {
    if (normalizedRoles.includes(role)) {
      return roleRedirectUrls[role];
    }
  }

  // Default to user app if no specific role matches
  return roleRedirectUrls.user;
}
